import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { Button, Grid } from "@mui/material";
import {
  SmartForm,
  SmartSubmit,
  SmartTextField,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { Choose } from "react-extras";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  OrganizationDetailsDto,
  useOrganizationApi,
} from "~/api/GeneratedApi";

interface FormProps {
  name: string;
  address: string;
}

interface Props {
  organizationData: OrganizationDetailsDto;
  onOrganizationUpdated: () => void;
}

const OrganizationDetailsTab = ({
  organizationData,
  onOrganizationUpdated,
}: Props): JSX.Element => {
  const organizationApi = useOrganizationApi();
  const { t } = useTranslation("organization-edit");
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<boolean>(false);

  const onSuccess = () => {
    enqueueSnackbar(t("edited"), { variant: "success" });
    setEditing(false);
    onOrganizationUpdated();
  };

  const onSubmit = (data: FormProps) => {
    return organizationApi.editOrganization({ ...data }, organizationData.id);
  };

  return (
    <div>
      <SmartForm<FormProps, ApiResponse>
        onSubmit={onSubmit}
        onSuccess={onSuccess}
        formProps={{
          defaultValues: {
            name: organizationData.name,
            address: organizationData.address ?? "",
          },
        }}
        noGrid
      >
        <Grid container spacing={2} direction="column">
          <Grid item xs={6}>
            <SmartTextField<FormProps>
              fullWidth
              disabled={!editing}
              name="name"
              label={t("organization-management:name")}
              required
            />
          </Grid>

          <Grid item xs={6}>
            <SmartTextField<FormProps>
              fullWidth
              disabled={!editing}
              name="address"
              label={t("organization-management:address")}
            />
          </Grid>

          <Grid item container xs={6} justifyContent="space-between">
            <Choose>
              <Choose.When condition={editing}>
                <Grid item>
                  <Button
                    startIcon={<CancelIcon />}
                    onClick={() => setEditing(false)}
                    color="primary"
                  >
                    {t("general:cancel")}
                  </Button>
                </Grid>
                <Grid item>
                  <SmartSubmit startIcon={<SaveIcon />}>
                    {t("general:save")}
                  </SmartSubmit>
                </Grid>
              </Choose.When>
              <Choose.Otherwise>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                  color="primary"
                >
                  {t("general:edit")}
                </Button>
              </Choose.Otherwise>
            </Choose>
          </Grid>
        </Grid>
      </SmartForm>
    </div>
  );
};

export default OrganizationDetailsTab;
